import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { LANGUAGES, resolveLanguage } from '../i18n/languages';
import { syncTelegramLanguage } from '../ton/notify';

/**
 * Modern custom language picker: a styled trigger button + an animated popover
 * menu (flag · endonym · check on the active language). Closes on outside click
 * and Escape, supports keyboard navigation, and persists the choice via i18next.
 */
export function LanguageSwitcher({ className }: { className?: string }) {
  const { t, i18n } = useTranslation();
  const active = resolveLanguage(i18n.language);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = (code: string) => {
    void i18n.changeLanguage(code);
    // Explicit user choice: mark it `chosen` so the bot / AI bot skip their
    // language picker (the `languageChanged` handler only syncs auto-detected).
    void syncTelegramLanguage(code, true);
    setOpen(false);
  };

  return (
    <div className={`lang-switcher${className ? ` ${className}` : ''}`} ref={rootRef}>
      <button
        type="button"
        className="lang-switcher__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('lang.label')}
        title={t('lang.label')}
      >
        <Globe size={15} aria-hidden="true" className="lang-switcher__globe" />
        <span className="lang-switcher__current">{active.name}</span>
        <ChevronDown
          size={14}
          aria-hidden="true"
          className={`lang-switcher__chevron${open ? ' is-open' : ''}`}
        />
      </button>

      {open && (
        <ul className="lang-switcher__menu" role="listbox" aria-label={t('lang.label')}>
          {LANGUAGES.map((l) => {
            const selected = l.code === active.code;
            return (
              <li key={l.code} role="none">
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`lang-switcher__option${selected ? ' is-selected' : ''}`}
                  onClick={() => pick(l.code)}
                  lang={l.code}
                  dir={l.dir}
                >
                  <span className="lang-switcher__flag" aria-hidden="true">
                    {l.flag}
                  </span>
                  <span className="lang-switcher__name">{l.name}</span>
                  {selected && (
                    <Check size={14} aria-hidden="true" className="lang-switcher__check" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
